import {
  PAGE_ROUTE_ABOUT_PRODUCT,
  PAGE_ROUTE_BLOG,
  PAGE_ROUTE_FAQ,
  PAGE_ROUTE_LEARN_MORE_BENEFITS,
  PAGE_ROUTE_LEARN_MORE_FINANCE,
  PAGE_ROUTE_LEARN_MORE_INDUSTRIES,
  PAGE_ROUTE_LEARN_MORE_CATEGORIES,
  PAGE_ROUTE_LEARN_MORE_INVENTORY,
  PAGE_ROUTE_LEARN_MORE_OPERATIONS,
  PAGE_ROUTE_LEARN_MORE_OVERVIEW,
  PAGE_ROUTE_LEARN_MORE_PLATFORM,
  PAGE_ROUTE_LEARN_MORE_REGIONS,
  PAGE_ROUTE_LEARN_MORE_TEAM,
  PAGE_ROUTE_ROLES_AND_ACCESS,
  PAGE_ROUTE_SOPS,
  PAGE_ROUTE_USER_GUIDE
} from "@kivunova/kivufrontendcommon";
import type { LearnMoreTopic } from "../types";

/** One topic per route under /learn-more/*. Section ids reference BLOG_SECTIONS. */
export const LEARN_MORE_TOPICS: LearnMoreTopic[] = [
  {
    id: "overview",
    route: PAGE_ROUTE_LEARN_MORE_OVERVIEW,
    titleKey: "learn-more-topic-overview-title",
    titleFallback: "What Kivunova FMS does",
    introKey: "learn-more-topic-overview-intro",
    introFallback:
      "A short look at what the system is, why businesses use it, and what you can manage in one place.",
    includeIntroParagraphs: true,
    sectionIds: ["what-is", "why-need", "why-choose"],
    calloutKey: "learn-more-topic-overview-callout",
    calloutFallback:
      "Ready to set up your company? Read the User guide for a step-by-step path, or open Run books for screenshot walkthroughs."
  },
  {
    id: "regions",
    route: PAGE_ROUTE_LEARN_MORE_REGIONS,
    titleKey: "learn-more-topic-regions-title",
    titleFallback: "Regions, languages, and currency",
    introKey: "learn-more-topic-regions-intro",
    introFallback:
      "Where Kivunova FMS is available today, which languages you can use, and how local currency and VAT work.",
    sectionIds: ["where-operates", "languages", "vat-currency"]
  },
  {
    id: "inventory",
    route: PAGE_ROUTE_LEARN_MORE_INVENTORY,
    titleKey: "learn-more-topic-inventory-title",
    titleFallback: "Products and stock",
    introKey: "learn-more-topic-inventory-intro",
    introFallback:
      "How your product catalog, stock in each store, and selling rules work together.",
    sectionIds: ["inventory"],
    calloutIngress: {
      prefixKey: "learn-more-topic-inventory-callout-prefix",
      prefixFallback:
        "Catalog products and product items are different things. Read ",
      route: PAGE_ROUTE_ABOUT_PRODUCT,
      linkKey: "learn-more-topic-inventory-callout-link",
      linkFallback: "Products vs. product items",
      suffixKey: "learn-more-topic-inventory-callout-suffix",
      suffixFallback:
        " for a plain comparison with examples and a hardware shop walkthrough. For parent and child categories, see ",
      testId: "learn-more-topic-inventory-about-product"
    },
    calloutIngressSecondary: {
      prefixKey: "learn-more-topic-inventory-categories-prefix",
      prefixFallback: "",
      route: PAGE_ROUTE_LEARN_MORE_CATEGORIES,
      linkKey: "learn-more-topic-inventory-categories-link",
      linkFallback: "Categories and subcategories",
      suffixKey: "learn-more-topic-inventory-categories-suffix",
      suffixFallback: ".",
      testId: "learn-more-topic-inventory-categories"
    }
  },
  {
    id: "operations",
    route: PAGE_ROUTE_LEARN_MORE_OPERATIONS,
    titleKey: "learn-more-topic-operations-title",
    titleFallback: "Stores, customers, and orders",
    introKey: "learn-more-topic-operations-intro",
    introFallback:
      "Day-to-day work: running branches, keeping customer and supplier records, and processing sales and purchases.",
    sectionIds: ["stores", "customers-suppliers", "orders"],
    calloutKey: "learn-more-topic-operations-callout",
    calloutFallback:
      "Need step-by-step help with a sale? See How to manage orders in Run books."
  },
  {
    id: "finance",
    route: PAGE_ROUTE_LEARN_MORE_FINANCE,
    titleKey: "learn-more-topic-finance-title",
    titleFallback: "Money and reports",
    introKey: "learn-more-topic-finance-intro",
    introFallback:
      "Track invoices, payments, and receipts, then use dashboards to see how the business is performing.",
    sectionIds: ["financial-reporting", "dashboards"]
  },
  {
    id: "team",
    route: PAGE_ROUTE_LEARN_MORE_TEAM,
    titleKey: "learn-more-topic-team-title",
    titleFallback: "People and access",
    introKey: "learn-more-topic-team-intro",
    introFallback:
      "Invite staff, assign roles, and control who can see or change company and store data.",
    sectionIds: ["people-roles"],
    calloutKey: "learn-more-topic-team-callout",
    calloutFallback:
      "See Roles & access for a full role list and who can add employees."
  },
  {
    id: "platform",
    route: PAGE_ROUTE_LEARN_MORE_PLATFORM,
    titleKey: "learn-more-topic-platform-title",
    titleFallback: "Plans, access, and help",
    introKey: "learn-more-topic-platform-intro",
    introFallback:
      "Subscriptions, signing in from anywhere, security, Ask Teta, and where to get more help.",
    sectionIds: ["subscriptions", "access", "security", "ask-teta", "help"]
  },
  {
    id: "industries",
    route: PAGE_ROUTE_LEARN_MORE_INDUSTRIES,
    titleKey: "learn-more-topic-industries-title",
    titleFallback: "Who uses Kivunova FMS",
    introKey: "learn-more-topic-industries-intro",
    introFallback:
      "Examples from retail, pharmacy, restaurants, and other businesses that run on Kivunova FMS.",
    sectionIds: ["industries"]
  },
  {
    id: "benefits",
    route: PAGE_ROUTE_LEARN_MORE_BENEFITS,
    titleKey: "learn-more-topic-benefits-title",
    titleFallback: "Why it helps your business",
    introKey: "learn-more-topic-benefits-intro",
    introFallback:
      "Less manual work, clearer numbers, and a modern way to run a growing business.",
    sectionIds: ["manual-work", "visibility", "future", "final-thoughts"]
  }
];

export const getLearnMoreTopicById = (
  topicId: string
): LearnMoreTopic | undefined =>
  LEARN_MORE_TOPICS.find((topic) => topic.id === topicId);

export const getLearnMoreTopicByRoute = (
  route: string
): LearnMoreTopic | undefined =>
  LEARN_MORE_TOPICS.find((topic) => topic.route === route);

/** Related links shown at the bottom of topic pages. */
export const LEARN_MORE_TOPIC_RELATED_LINKS: Record<
  string,
  { route: string; labelKey: string; labelFallback: string }[]
> = {
  overview: [
    {
      route: PAGE_ROUTE_USER_GUIDE,
      labelKey: "learn-more-related-user-guide",
      labelFallback: "User guide"
    },
    {
      route: PAGE_ROUTE_SOPS,
      labelKey: "learn-more-related-run-books",
      labelFallback: "Run books"
    }
  ],
  inventory: [
    {
      route: PAGE_ROUTE_ABOUT_PRODUCT,
      labelKey: "learn-more-related-about-product",
      labelFallback: "Products vs. product items"
    },
    {
      route: PAGE_ROUTE_LEARN_MORE_CATEGORIES,
      labelKey: "learn-more-related-categories",
      labelFallback: "Categories and subcategories"
    },
    {
      route: PAGE_ROUTE_SOPS,
      labelKey: "learn-more-related-run-books",
      labelFallback: "Run books"
    }
  ],
  operations: [
    {
      route: PAGE_ROUTE_SOPS,
      labelKey: "learn-more-related-run-books",
      labelFallback: "Run books"
    }
  ],
  team: [
    {
      route: PAGE_ROUTE_ROLES_AND_ACCESS,
      labelKey: "learn-more-related-roles",
      labelFallback: "Roles & access"
    }
  ],
  platform: [
    {
      route: PAGE_ROUTE_USER_GUIDE,
      labelKey: "learn-more-related-user-guide",
      labelFallback: "User guide"
    },
    {
      route: PAGE_ROUTE_FAQ,
      labelKey: "learn-more-related-faq",
      labelFallback: "FAQ"
    }
  ],
  benefits: [
    {
      route: PAGE_ROUTE_BLOG,
      labelKey: "learn-more-related-all-topics",
      labelFallback: "All learn more topics"
    }
  ]
};
